// Markdown restrito → blocos.
//
// Os relatórios especiais são texto corrido escrito sob encomenda — "pode ser
// clônus?", "por que ela não lembra da gente?" — e o jeito mais barato de
// escrever texto com títulos, listas e tabelas é Markdown. Mas o documento é
// desenhado duas vezes, na tela em React e no papel em pdfmake, e nenhum dos
// dois lê Markdown. Este módulo lê UMA vez e entrega blocos que os dois
// desenham igual.
//
// É de propósito um subconjunto: títulos, parágrafos, listas, tabelas,
// citações, régua, negrito, itálico e link. Nada de HTML embutido — o texto
// nunca vira HTML, então não há o que injetar. E o que o módulo não entende
// fica como texto, visível: erro de escrita aparece, não some.

export interface Trecho {
  texto: string;
  negrito?: boolean;
  italico?: boolean;
  /** Só http(s). Qualquer outra coisa entre parênteses fica como texto. */
  link?: string;
}

export type Bloco =
  | { tipo: 'titulo'; nivel: 1 | 2 | 3; texto: Trecho[] }
  | { tipo: 'paragrafo'; texto: Trecho[] }
  | { tipo: 'lista'; ordenada: boolean; itens: Trecho[][] }
  | { tipo: 'tabela'; cabecalho: Trecho[][]; linhas: Trecho[][][] }
  | { tipo: 'citacao'; paragrafos: Trecho[][] }
  | { tipo: 'regua' };

const TITULO = /^(#{1,6})\s+(.*\S)\s*$/;
const REGUA = /^\s*([-*_])(\s*\1){2,}\s*$/;
const ITEM = /^\s{0,3}[-*+•]\s+(.*)$/;
const ITEM_NUMERADO = /^\s{0,3}\d{1,3}[.)]\s+(.*)$/;
const CITACAO = /^\s{0,3}>\s?(.*)$/;
const LINHA_DE_TABELA = /^\s*\|.*\|\s*$/;
const SEPARADOR_DE_TABELA = /^\s*\|?(\s*:?-{2,}:?\s*\|)*\s*:?-{2,}:?\s*\|?\s*$/;
const LINK = /^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/;

/** O texto de uma linha, com negrito, itálico e link resolvidos. */
export function lerTrechos(linha: string): Trecho[] {
  const saida: Trecho[] = [];
  let negrito = false;
  let italico = false;
  let atual = '';
  const estilo = () => ({ ...(negrito ? { negrito: true } : {}), ...(italico ? { italico: true } : {}) });
  const despejar = () => {
    if (!atual) return;
    saida.push({ texto: atual, ...estilo() });
    atual = '';
  };

  let i = 0;
  while (i < linha.length) {
    const c = linha[i]!;
    // Barra invertida protege o caractere seguinte: "\*" é um asterisco.
    if (c === '\\' && i + 1 < linha.length) { atual += linha[i + 1]; i += 2; continue; }
    if (linha.startsWith('**', i)) { despejar(); negrito = !negrito; i += 2; continue; }
    if (c === '*') {
      // Abre itálico só com texto colado ("*assim*"); fecha sempre que está
      // aberto. "5 * 3" fica como está.
      const proximo = linha[i + 1];
      if (italico || (proximo !== undefined && proximo !== ' ')) { despejar(); italico = !italico; i += 1; continue; }
    }
    if (c === '[') {
      const m = LINK.exec(linha.slice(i));
      if (m) {
        despejar();
        for (const t of lerTrechos(m[1]!)) saida.push({ ...t, ...estilo(), link: m[2]! });
        i += m[0].length;
        continue;
      }
    }
    // Crase é só marcação de código: some, o texto fica.
    if (c === '`') { i += 1; continue; }
    atual += c;
    i += 1;
  }
  despejar();
  return saida;
}

/** As células de uma linha de tabela. "\|" dentro da célula é uma barra. */
function celulas(linha: string): Trecho[][] {
  return linha.trim()
    .replace(/^\|/, '').replace(/\|$/, '')
    .replace(/\\\|/g, ' ')
    .split('|')
    .map((c) => lerTrechos(c.replace(/ /g, '|').trim()));
}

const ehBranca = (l: string | undefined) => l === undefined || l.trim() === '';
const comecaBloco = (l: string) =>
  TITULO.test(l) || REGUA.test(l) || LINHA_DE_TABELA.test(l) || CITACAO.test(l) || ITEM.test(l) || ITEM_NUMERADO.test(l);

export function lerMarkdown(texto: string): Bloco[] {
  const linhas = texto.replace(/\r\n?/g, '\n').split('\n');
  const blocos: Bloco[] = [];
  let i = 0;

  while (i < linhas.length) {
    const l = linhas[i]!;
    if (ehBranca(l)) { i += 1; continue; }

    const titulo = TITULO.exec(l);
    if (titulo) {
      blocos.push({ tipo: 'titulo', nivel: Math.min(3, titulo[1]!.length) as 1 | 2 | 3, texto: lerTrechos(titulo[2]!) });
      i += 1;
      continue;
    }
    if (REGUA.test(l)) { blocos.push({ tipo: 'regua' }); i += 1; continue; }

    if (LINHA_DE_TABELA.test(l)) {
      const brutas: string[] = [];
      while (i < linhas.length && LINHA_DE_TABELA.test(linhas[i]!)) { brutas.push(linhas[i]!); i += 1; }
      let cabecalho: Trecho[][] = [];
      let corpo = brutas;
      if (brutas.length >= 2 && SEPARADOR_DE_TABELA.test(brutas[1]!)) {
        cabecalho = celulas(brutas[0]!);
        corpo = brutas.slice(2);
      }
      blocos.push({ tipo: 'tabela', cabecalho, linhas: corpo.filter((r) => !SEPARADOR_DE_TABELA.test(r)).map(celulas) });
      continue;
    }

    if (CITACAO.test(l)) {
      // Cada linha da citação é um parágrafo: as perguntas para a visita vêm
      // uma por linha, e é assim que precisam sair.
      const paragrafos: Trecho[][] = [];
      let m: RegExpExecArray | null;
      while (i < linhas.length && (m = CITACAO.exec(linhas[i]!))) {
        const conteudo = m[1]!.trim();
        if (conteudo) paragrafos.push(lerTrechos(conteudo));
        i += 1;
      }
      blocos.push({ tipo: 'citacao', paragrafos });
      continue;
    }

    if (ITEM.test(l) || ITEM_NUMERADO.test(l)) {
      const ordenada = ITEM_NUMERADO.test(l);
      const padrao = ordenada ? ITEM_NUMERADO : ITEM;
      const outro = ordenada ? ITEM : ITEM_NUMERADO;
      const itens: string[] = [];
      while (i < linhas.length && !ehBranca(linhas[i])) {
        const atual = linhas[i]!;
        const item = padrao.exec(atual);
        if (item) itens.push(item[1]!.trim());
        else if (itens.length > 0 && !outro.test(atual) && !TITULO.test(atual) && !LINHA_DE_TABELA.test(atual) && !CITACAO.test(atual)) {
          // Linha sem marcador logo abaixo de um item continua o item.
          itens[itens.length - 1] += ` ${atual.trim()}`;
        } else break;
        i += 1;
      }
      blocos.push({ tipo: 'lista', ordenada, itens: itens.map(lerTrechos) });
      continue;
    }

    // Parágrafo: junta as linhas até a próxima em branco ou o próximo bloco.
    const partes: string[] = [];
    while (i < linhas.length && !ehBranca(linhas[i])) {
      const atual = linhas[i]!;
      if (partes.length > 0 && comecaBloco(atual)) break;
      partes.push(atual.trim());
      i += 1;
    }
    blocos.push({ tipo: 'paragrafo', texto: lerTrechos(partes.join(' ')) });
  }
  return blocos;
}

/** O texto sem marcação — para título de seção, nome de arquivo, busca. */
export const textoPuro = (trechos: Trecho[]): string => trechos.map((t) => t.texto).join('');
